import React, { Children, FunctionComponent, isValidElement } from 'react';
import cx from 'classnames';
import Checkmark from '@clearkit/icons/glyphs/Checkmark';
import { motion } from 'framer-motion';

interface StepperContext {
  index: number;
}

const stepperContext = React.createContext<StepperContext>({ index: 0 });

export interface CKStepperProps {
  index: number;
}

export interface CKStepperComposition {
  Header: FunctionComponent<HeaderProps>;
  Step: FunctionComponent<StepProps>;
  Content: FunctionComponent;
}

export const CKStepper: FunctionComponent<CKStepperProps> &
  CKStepperComposition = ({ children, index }) => {
  let stepIds: string[] = [];
  Children.forEach(children, (child: React.ReactNode) => {
    if (!child || !isValidElement(child)) return;
    if (child && child.type === Step) {
      stepIds.push(child.props.id);
    }
  });

  return (
    <stepperContext.Provider
      value={{
        index,
      }}
    >
      <div className="ck-stepper">
        {Children.map(children, (child: React.ReactNode) => {
          if (!isValidElement(child)) return null;
          if (child.type === Step) {
            return React.cloneElement(child, {
              index: stepIds.indexOf(child.props.id),
            });
          }
          return null;
        })}
      </div>
    </stepperContext.Provider>
  );
};

export interface HeaderIconProps {
  isComplete: boolean;
  isActive: boolean;
}

type IconProps = {
  (props: HeaderIconProps): React.ReactNode;
};

export interface HeaderProps {
  heading: React.ReactNode;
  icon: IconProps | React.ReactNode;
  indicator?: React.ReactNode;
  status?: React.ReactNode;
  index?: number;
}

const Header: FunctionComponent<HeaderProps> = ({
  heading,
  icon,
  indicator,
  status,
  index = 0,
}) => {
  const { index: activeStep } = React.useContext(stepperContext);

  const isComplete = index < activeStep;
  const isActive = index === activeStep;

  const stepIcon =
    typeof icon === 'function' ? icon({ isComplete, isActive }) : icon;

  return (
    <div className="ck-stepper-header">
      <div className="ck-stepper-heading">
        <div className="ck-stepper-heading-icon">{stepIcon}</div>
        <div className="ck-stepper-heading-title">{heading}</div>
      </div>
      {status && isComplete && (
        <div className="ck-stepper-step-status">{status}</div>
      )}
      <div className="ck-stepper-step-indicator">{indicator}</div>
    </div>
  );
};

export interface StepProps {
  isComplete?: boolean;
  index?: number;
  id: string;
  icon: IconProps | React.ReactNode;
  heading: React.ReactNode;
  content: React.ReactNode;
  status?: React.ReactNode;
}

const Step: FunctionComponent<StepProps> = ({
  heading,
  index = 0,
  icon,
  content,
  status,
}) => {
  const { index: activeStep } = React.useContext(stepperContext);

  const isActive = index === activeStep;
  const isComplete = index < activeStep;

  const stepClass = cx('ck-stepper-step', {
    'ck-stepper-step--active': isActive,
    'ck-stepper-step--complete': isComplete,
  });

  const indicator = isComplete ? <Checkmark /> : index + 1;

  const variants = {
    active: {
      height: 'auto',
    },
    inactive: {
      height: 0,
    },
  };

  const variant = isActive ? 'active' : 'inactive';

  return (
    <div className={stepClass}>
      <Header
        heading={heading}
        icon={icon}
        indicator={indicator}
        status={status}
        index={index}
      />
      <motion.div
        transition={{
          ease: 'easeInOut',
          duration: 0.2,
        }}
        key={index}
        initial={false}
        variants={variants}
        animate={variant}
      >
        {isActive && <Content>{content}</Content>}
      </motion.div>
    </div>
  );
};

const Content: FunctionComponent = ({ children }) => <div>{children}</div>;

CKStepper.Header = Header;
CKStepper.Step = Step;
CKStepper.Content = Content;
